import React, { useState, useEffect, useRef } from "react";
import apiClient from "../../AppClient";
import { Container, Form, Button, Table, Alert, Spinner, Modal, Pagination } from "react-bootstrap";
import { FaSearch } from "react-icons/fa";

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [newExpenseName, setNewExpenseName] = useState("");
  const [newExpenseAmount, setNewExpenseAmount] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState({ id: null, name: "", amount: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const itemsPerPage = 10;

  const nameRef = useRef(null);
  const amountRef = useRef(null);

  // Fetch all expenses
  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/expenses/");
      setExpenses(response.data);
      setFilteredExpenses(response.data);
      setError(null);
    } catch (error) {
      setError(error.response?.data?.detail || "فشل في تحميل المصروفات.");
    } finally {
      setLoading(false);
    }
  };

  // Search functionality
  useEffect(() => {
    const filtered = expenses.filter(expense =>
      expense.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredExpenses(filtered);
    setCurrentPage(1);
  }, [searchTerm, expenses]);

  // Add a new expense
  const addExpense = async () => {
    const name = nameRef.current.value;

    if (!name) {
      return setError("الرجاء إدخال اسم المصروف");
    }

    try {
      await apiClient.post("/expenses/add/", {
        name: newExpenseName,
      });
      fetchExpenses();
      setNewExpenseName("");
      setNewExpenseAmount("");
      setError(null);
      setCurrentPage(1);
    } catch (error) {
      setError(error.response?.data?.detail || "فشل في إضافة المصروف.");
    }
  };

  // Open edit modal
  const openEditModal = (expense) => {
    setSelectedExpense({ id: expense.id, name: expense.name, amount: expense.amount });
    setShowEditModal(true);
  };

  // Save edited expense
  const saveEditedExpense = async () => {
    if (!selectedExpense.name) {
      return setError("الرجاء إدخال اسم المصروف");
    }

    try {
      await apiClient.put(`/expenses/${selectedExpense.id}/update/`, {
        name: selectedExpense.name,
        amount: parseFloat(selectedExpense.amount),
      });
      fetchExpenses();
      setShowEditModal(false);
      setError(null);
    } catch (error) {
      setError(error.response?.data?.detail || "فشل في تعديل المصروف.");
    }
  };

  // Delete a expense
  const deleteExpense = async (id) => {
    const confirmDelete = window.confirm("هل أنت متأكد أنك تريد حذف هذا المصروف؟");
    if (!confirmDelete) return;

    try {
      await apiClient.delete(`/expenses/${id}/delete/`);
      fetchExpenses();
      if (filteredExpenses.length % itemsPerPage === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    } catch (error) {
      setError(error.response?.data?.detail || "فشل في حذف المصروف.");
    }
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredExpenses.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredExpenses.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  useEffect(() => {
    fetchExpenses();
  }, []);

  return (
    <Container style={{ direction: "rtl", textAlign: "right", padding: "20px", fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}>
      <h1 style={{ color: "darkgreen", marginBottom: "20px", fontWeight: "bold" }}>المصاريف</h1>

      {/* Search Bar */}
      <div className="mb-3" style={{ maxWidth: "400px" }}>
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            placeholder="ابحث عن مصروف..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ borderColor: "darkgreen" }}
          />
          <span
            className="input-group-text"
            style={{ backgroundColor: "darkgreen", color: "white" }}
          >
            <FaSearch />
          </span>
        </div>
      </div>

      {/* Add Expense Form */}
      <Form.Group className="mb-3">
        <Form.Control
          ref={nameRef}
          type="text"
          placeholder="أدخل اسم المصروف"
          value={newExpenseName}
          onChange={(e) => setNewExpenseName(e.target.value)}
          style={{ marginBottom: "10px", borderColor: "darkgreen" }}
        />
        <Button
          onClick={addExpense}
          style={{
            backgroundColor: "darkgreen",
            borderColor: "darkgreen"
          }}
        >
          إضافة مصروف
        </Button>
      </Form.Group>

      {/* Error Message */}
      {error && <Alert variant="danger">{error}</Alert>}

      {/* Loading Spinner */}
      {loading ? (
        <div className="text-center">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <>
          {/* Expenses Table */}
          <Table striped bordered hover responsive>
            <thead style={{ backgroundColor: "darkgreen", color: "white" }}>
              <tr>
                <th>#</th>
                <th>اسم المصروف</th>
                <th>الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.length === 0 ? (
                <tr>
                  <td colSpan="3">لا توجد مصروفات حاليا</td>
                </tr>
              ) : (
                currentItems.map((expense, index) => (
                  <tr key={expense.id}>
                    <td>{indexOfFirstItem + index + 1}</td>
                    <td>{expense.name}</td>
                    <td>
                      <Button
                        size="sm"
                        className="btn btn-info bg-info mx-1"
                        onClick={() => openEditModal(expense)}
                      >
                        تعديل
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => deleteExpense(expense.id)}
                        className="btn btn-danger bg-danger mx-1"
                      >
                        <i className="fas fa-trash"></i> حذف
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {/* Pagination */}
          {filteredExpenses.length > itemsPerPage && (
            <Pagination className="justify-content-center">
              <Pagination.First
                onClick={() => paginate(1)}
                disabled={currentPage === 1}
                style={{ color: "darkgreen" }}
              />
              <Pagination.Prev
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                style={{ color: "darkgreen" }}
              />

              {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                <Pagination.Item
                  key={number}
                  active={number === currentPage}
                  onClick={() => paginate(number)}
                  style={{
                    backgroundColor: number === currentPage ? "darkgreen" : "white",
                    color: number === currentPage ? "white" : "darkgreen",
                    borderColor: "darkgreen"
                  }}
                >
                  {number}
                </Pagination.Item>
              ))}

              <Pagination.Next
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{ color: "darkgreen" }}
              />
              <Pagination.Last
                onClick={() => paginate(totalPages)}
                disabled={currentPage === totalPages}
                style={{ color: "darkgreen" }}
              />
            </Pagination>
          )}
        </>
      )}

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton style={{ borderColor: "darkgreen" }}>
          <Modal.Title style={{ color: "darkgreen" }}>تعديل المصروف</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control
            type="text"
            value={selectedExpense.name}
            onChange={(e) =>
              setSelectedExpense({ ...selectedExpense, name: e.target.value })
            }
            placeholder="أدخل الاسم الجديد"
            style={{ marginBottom: "10px", borderColor: "darkgreen" }}
          />
        </Modal.Body>
        <Modal.Footer style={{ borderColor: "darkgreen" }}>
          <Button
            variant="secondary"
            onClick={() => setShowEditModal(false)}
            style={{
              backgroundColor: "white",
              color: "darkgreen",
              borderColor: "darkgreen"
            }}
          >
            إلغاء
          </Button>
          <Button
            onClick={saveEditedExpense}
            style={{
              backgroundColor: "darkgreen",
              borderColor: "darkgreen"
            }}
          >
            حفظ
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Expenses;